import { createGlobalStyle } from "styled-components";

const GlobalStyle = createGlobalStyle`
    /* ===== RESPONSIVE FONT SIZING ===== */
    html {
        font-size: 16px; /* Base size: 1rem = 16px */

        /* Mobile */
        @media (max-width: 480px) {
            font-size: 14px; /* 1rem = 14px on mobile */
        }

        /* Tablet */
        @media (min-width: 481px) and (max-width: 768px) {
            font-size: 15px; /* 1rem = 15px on tablet */
        }

        /* Desktop */
        @media (min-width: 1440px) {
            font-size: 18px; /* 1rem = 18px on large screens */
        }
    }

    /* ===== GLOBAL RESET ===== */
    * {
        padding: 0;
        margin: 0;
        box-sizing: border-box;
    }

    /* ===== BASE STYLES ===== */
    body {
        font-size: 1rem; /* 16px */
        line-height: 1.6;
    }

    a {
        text-decoration: none;
        color: inherit;
    }

    li {
        list-style: none;
    }

    /* ===== TYPOGRAPHY ===== */
    h1 {
        font-size: 2.5rem;   /* 40px */
        line-height: 1.2;

        @media (max-width: 768px) {
            font-size: 2rem; /* 32px on mobile */
        }
    }

    h2 {
        font-size: 2rem;     /* 32px */
        line-height: 1.3;

        @media (max-width: 768px) {
            font-size: 1.75rem; /* 28px on mobile */
        }
    }

    h3 {
        font-size: 1.75rem;  /* 28px */
        line-height: 1.3;

        @media (max-width: 768px) {
            font-size: 1.5rem; /* 24px on mobile */
        }
    }

    h4 {
        font-size: 1.5rem;   /* 24px */
        line-height: 1.4;
    }

    h5 {
        font-size: 1.25rem;  /* 20px */
        line-height: 1.4;
    }

    h6 {
        font-size: 1.125rem; /* 18px */
        line-height: 1.4;
    }

    p {
        font-size: 1rem;     /* 16px */
        line-height: 1.6;
    }

    small {
        font-size: 0.875rem !important; /* 14px */
    }

    .text_center {
        text-align: center;
    }

    .sub_title {
        color: var(--primary);
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 1.5px;
        font-size: 0.75rem;
        margin-bottom: 1rem;
        text-align: center;
    }

    .title {
        text-align: center;
        margin-bottom: 1rem;
        font-weight: 900;
    }

    .section_intro {
        color: var(--gray);
        text-align: center;
        font-size: 0.875rem;
    }

    /* ===== LAYOUT UTILITIES ===== */
    .full_width {
        width: 100%;
    }

    .flex {
        display: flex;
    }

    .flex_center {
        align-items: center;
    }

    .justify_center {
        justify-content: center;
    }

    .justify_end {
        justify-content: end;
    }

    .space_between {
        justify-content: space-between;
    }

    /* ===== SPACING UTILITIES (REM-BASED) ===== */
    .mt5 {
        margin-top: 0.3125rem;  /* 5px */
    }

    .mt10 {
        margin-top: 0.625rem;   /* 10px */
    }

    .mt20 {
        margin-top: 1.25rem;    /* 20px */
    }

    .mt30 {
        margin-top: 1.875rem;   /* 30px */
    }

    .mt40 {
        margin-top: 2.5rem;     /* 40px */
    }

    .mb10 {
        margin-bottom: 0.625rem;
    }

    .mb20 {
        margin-bottom: 1.25rem;
    }

    .mb30 {
        margin-bottom: 1.875rem;
    }

    .ml10 {
        margin-left: 0.625rem;
    }

    .ml20 {
        margin-left: 1.25rem;
    }

    .ml30 {
        margin-left: 1.875rem;
    }

    .ml40 {
        margin-left: 1.875rem;
    }

    .p10 {
        padding: 0.625rem;
    }

    .p20 {
        padding: 1.25rem;
    }

    .p30 {
        padding: 1.875rem;
    }

    .gapy_10 {
        gap: 0px 10px;
    }

    .gapy_20 {
        gap: 0px 20px;
    }

    /* ===== CONTAINER ===== */
    .container {
        width: 100%;
        max-width: 81.25rem; /* 1300px */
        margin: 0 auto;
        padding: 0 1rem;     /* 16px horizontal padding */

        @media (max-width: 768px) {
            padding: 0 0.75rem; /* 12px on mobile */
        }
    }

     /* ===== BUTTONS ===== */
    /* Base button class with common styles */
    .btn {
        padding: 0.6rem 1.2rem;
        border-radius: 8px;
        font-weight: 500;
        font-size: 0.875rem;
        transition: all 0.2s ease;
        display: inline-block;
        line-height: 1;
        text-decoration: none;
        outline: none;
        cursor: pointer;
        border: none;
    }

    /* Header buttons - Login & Register */
    .header_btn {
        background: var(--dark);
        color: white;
        font-weight: 500;
        padding: 0.4rem .8rem;
        border-radius: 6px;
        transition: background 0.2s ease-in-out;
        border: 1px solid var(--dark);
        outline: none;
        font-size: 0.9rem;
    }

    .header_btn:hover {
        background: transparent;
        color: var(--dark);
        cursor: pointer;
    }

    /* Tertiary button - Footer CTA */
    .tertiary_button {
        background: white;
        color: var(--dark);
        border: 1px solid white;
    }

    .tertiary_button:hover {
        background: transparent;
        color: white;
    }

    /* Primary button - Main CTA */
    .primary_button {
        background: linear-gradient(135deg, #667eea, #764ba2);
        color: white;
    }

    .primary_button:hover {
       opacity: 0.95;
    }

    /* Secondary button - Outline variant */
    .secondary_button {
        background: transparent;
        color: #667eea;
        border: 1px solid #667eea;
    }

    .secondary_button:hover {
        background: #667eea;
        color: white;
    }

    /* Complementary button - Secondary CTA */
    .complementary_button {
        background: white;
        color: #0f172a;
        font-weight: 400;
        border: 1px solid #e2e8f0;
    }

    .complementary_button:hover {
        border-color: #0f172a;
    }

    /* Danger button */
    .danger_button {
        color: white;
        background: #dc2626
    }

    .danger_button:hover {
        background: transparent;
        color: #dc2626;
        border: 1px solid #dc2626;
    }
`;

export default GlobalStyle;
